import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatCardModule } from '@angular/material/card';
import { MatMenuModule } from '@angular/material/menu';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { Router } from '@angular/router';
import { finalize } from 'rxjs/operators';
import { ApiservicesService } from '../../../Services/Apis/apiservices.service';
import { DynamicPageCreationComponent } from '../../../components/webpagecreation/dynamic-page-creation/dynamic-page-creation.component';
import { WebsiteNameService } from '../../../inputfileds/form_services/websitemanagementService02/webSitesName/website-name.service';
import { PermissionService } from '../../../components/Authentication/services/permission.service';
import { TranslateDirective } from '../../../components/Utilites/Directives/translate.directive';

interface WebsiteComponent {
  id: string;
  type: string;
  title: string;
  image_url: string;
}
interface WebsitePage {
  id: string;
  page_name: string;
  components: WebsiteComponent[];
}
interface WebsiteData {
  id: string;
  name: string;
  pages: WebsitePage[];
}

@Component({
  selector: 'app-website-name-list',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatListModule,
    MatCardModule,
    MatMenuModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    TranslateDirective
  ],
  templateUrl: './website-name-list.component.html',
  styleUrls: ['./website-name-list.component.scss'],
  providers: [ApiservicesService],
})
export class WebsiteNameListComponent implements OnInit {
  websiteNames: any[] = [];
  actiondata: any;
  // UI state
  loading = false;
  errorMsg = '';

  constructor(
    private dialog: MatDialog,
    private router: Router,
    private apiServices: ApiservicesService,
    private permissionService: PermissionService,
    private websiteNameQuestion: WebsiteNameService
  ) { }

  ngOnInit(): void {
    this.actiondata = this.permissionService.getactiondata();
    this.getWebsiteName();
  }

  getWebsiteName(): void {
    this.loading = true;
    this.errorMsg = '';
    this.apiServices
      .getWebsiteNameList()
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (res: any) => {
          this.websiteNames = res?.rows ?? [];
          sessionStorage.setItem(
            'websiteDetails',
            JSON.stringify(this.websiteNames)
          );
        },
        error: (err) => {
          console.error('Error loading websites:', err);
          this.websiteNames = [];
          this.errorMsg = 'Failed to load websites. Please try again.';
        },
      });
  }

  // for retry button
  reload(): void {
    this.getWebsiteName();
  }

  openAddWebsiteDialog(): void {
    const dialogRef = this.dialog.open(DynamicPageCreationComponent, {
      width: '600px',
      data: {
        pageTitle: 'Add Website',
        questions$: this.websiteNameQuestion.getWebsiteNameform(),
        submitButtonText: 'Add Website',
        TabButton: false,
      },
    });
    dialogRef.afterClosed().subscribe((formData) => {
      this.loading = true;
      this.apiServices
        .addWebsiteName(formData)
        .pipe(finalize(() => (this.loading = false)))
        .subscribe({
          next: (response: any) => {
            this.websiteNames.push(response);
            this.getWebsiteName();
            dialogRef.close();
          },
          error: (err) => {
            console.error('Add website failed:', err);
          },
        });
    });

    // Handle explicit cancel
    dialogRef.componentInstance.cancel.subscribe(() => {
      dialogRef.close();
    });
  }

  openEditWebsiteDialog(website: any): void {
    const dialogRef = this.dialog.open(DynamicPageCreationComponent, {
      width: '600px',
      data: {
        pageTitle: 'Update Website Name',
        questions$: this.websiteNameQuestion.getWebsiteNameform(),
        submitButtonText: 'Update Website Name',
        initialData: website,
      },
    });

    dialogRef.afterClosed().subscribe((formData) => {
      if (!formData.id) {
        console.error('No ID provided for update');
        return;
      }
      this.loading = true;
      this.apiServices
        .updateWebsiteName(formData.id, formData)
        .pipe(finalize(() => (this.loading = false)))
        .subscribe({
          next: (response: any) => {
            const index = this.websiteNames.findIndex(
              (w: any) => w.id === response.id
            );
            if (index !== -1) this.websiteNames[index] = response;
            dialogRef.close();
            this.getWebsiteName();
          },
          error: (err) => {
            console.error('Update website failed:', err);
          },
        });
    });

    dialogRef.componentInstance.cancel.subscribe(() => {
      dialogRef.close();
    });
  }

  viewWebsitePages(websiteId: any): void {
    this.router.navigate([`websites/WebpageList`], {
      queryParams: { websiteId },
    });
  }

  onMenuClick(event: MouseEvent): void {
    event.stopPropagation();
  }

  deleteWebsite(websiteNameID: any): void {
    this.loading = true;
    this.apiServices
      .deleteWebsiteNameData(websiteNameID)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: () => {
          this.websiteNames = this.websiteNames.filter(
            (w) => w.id !== websiteNameID
          );
        },
        error: (err) => {
          console.error('Error deleting website:', err);
        },
      });
  }
}
